import { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router';

import { GroupTenantContextMenuRenderer } from '@/backoffice/features/groups/components/group-tabs/group-tenants-list.renderer';
import { UNGROUPED_RESOURCES_GROUP_ID } from '@/backoffice/features/groups/groups.constants';
import { useTenantSubscriptions } from '@/backoffice/features/tenants/hooks/use-tenant-subscriptions/use-tenant-subscriptions';
import { useTenantsListDefinitions } from '@/backoffice/features/tenants/hooks/use-tenants-list-definitions/use-tenants-list-definitions';
import { useTenantsTransformer } from '@/backoffice/features/tenants/hooks/use-tenants-transformer/use-tenants-transformer';
import { TTransformedTenant } from '@/backoffice/features/tenants/hooks/use-tenants-transformer/use-tenants-transformer.types';
import { useReadTenantsForSuperTenantQuery } from '@/backoffice/redux/api/backoffice/backoffice-api';
import {
  FilterOperatorEnum,
  TenantFilterInput,
} from '@/backoffice/types/types.generated';
import { DataTableProvider } from '@/shared/components/ui/data-table/context/data-table-context';
import { TData } from '@/shared/components/ui/data-table/context/data-table-context.types';
import { InnerDataTable } from '@/shared/components/ui/data-table/variant/inner-data-table/inner-data-table';
import { usePaginationQueryParams } from '@/shared/components/ui/pagination/hooks/use-pagination-query-params/use-pagination-query-params';
import {
  FilterScopeProvider,
  useFilterScope,
} from '@/shared/features/filter-preferences/context/filter-scope-context';
import { GroupResourceBatchActions } from '@/shared/features/groups/components/group-resource-batch-actions/group-resource-batch-actions';
import { TGroupResourceItem } from '@/shared/features/groups/context/groups-config-context.types';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { PaginationInput } from '@/shared/types/types.generated';

const GroupTenantsListInner = () => {
  const { isReady } = useFilterScope();
  const { groupId } = useParams();
  const { page, pageSize } = usePaginationQueryParams();
  const pagination: PaginationInput = { page, pageSize };
  const { can } = usePermissionCheck();

  const isUngrouped = groupId === UNGROUPED_RESOURCES_GROUP_ID;
  const canManageMembers = can(
    'backofficeGroup:updateGroup',
    isUngrouped ? undefined : groupId,
  );
  const filter: TenantFilterInput = isUngrouped
    ? { groupId: { doNotIgnoreNullable: [FilterOperatorEnum.Eq] } }
    : { groupId: { eq: groupId } };

  const { data, isLoading, isFetching } = useReadTenantsForSuperTenantQuery(
    { filter, pagination },
    { skip: !isReady || !groupId },
  );
  const { transformTenant } = useTenantsTransformer();
  const tenants = data?.readTenantsForSuperTenant;
  const rowData = useTenantSubscriptions(
    tenants?.items?.map(transformTenant) ?? [],
  );

  const colDefs = useTenantsListDefinitions({
    hideGroupColumn: true,
    actionsRenderer: GroupTenantContextMenuRenderer,
  });

  const _data: TData<TTransformedTenant> = {
    isLoading,
    isFetching,
    items: rowData,
    pages: tenants?.pages ?? 0,
    itemCount: tenants?.itemCount ?? 0,
  };

  const batchActions = (
    selectedItems: TTransformedTenant[],
    setSelectedItems: Dispatch<SetStateAction<TTransformedTenant[]>>,
  ) => (
    <GroupResourceBatchActions
      selectedItems={
        selectedItems.map(({ raw }) => raw) as TGroupResourceItem[]
      }
      onSuccess={() => setSelectedItems([])}
    />
  );

  return (
    <DataTableProvider<TTransformedTenant>
      value={{
        data: _data,
        getItemId: (item) => item.tenantId,
        getItemLink: (item) => `/${item.tenantId}`,
        disableItemClick: (item) =>
          !item.isActive ||
          !can('backofficeTenant:selectTenant', item.tenantId),
      }}
      colDefs={colDefs}
    >
      <InnerDataTable<TTransformedTenant>
        batchActions={canManageMembers ? batchActions : undefined}
      />
    </DataTableProvider>
  );
};

export const GroupTenantsList = () => (
  <FilterScopeProvider scopeKey="group.tenants.list">
    <GroupTenantsListInner />
  </FilterScopeProvider>
);
